import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ClinicWhatsAppApplicationReminderRuleRow = {
  clinic_id: string;
  is_active: boolean;
  offset_unit: "days" | "weeks" | "months";
  offset_value: number;
  send_time: string;
  timezone: string;
  updated_at: string | null;
};

export type ClinicWhatsAppApplicationReminderRuleInput = {
  isActive: boolean;
  offsetUnit: "days" | "weeks" | "months";
  offsetValue: number;
  sendTime: string;
  timezone: string;
};

export type NextWhatsAppApplicationReminderTriggerRow = {
  next_trigger_at: string;
  pet_application_id: string;
  due_date: string;
};

type LooseSelectResult = Promise<{ data: unknown; error: Error | null }>;

type LooseTableClient = {
  select: (columns: string) => {
    eq: (column: string, value: string) => {
      maybeSingle: () => LooseSelectResult;
    };
  };
  upsert: (payload: Record<string, unknown>, options: { onConflict: string }) => Promise<{ error: Error | null }>;
};

type LooseRpcClient = {
  rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: Error | null }>;
};

const getLooseTableClient = (table: string) => {
  return (supabase as unknown as { from: (name: string) => LooseTableClient }).from(table);
};

const getLooseRpcClient = () => supabase as unknown as LooseRpcClient;

export function useClinicWhatsAppApplicationReminderRule(clinicId: string | null) {
  return useQuery({
    queryKey: ["clinic-whatsapp-application-reminder-rule", clinicId],
    queryFn: async (): Promise<ClinicWhatsAppApplicationReminderRuleRow | null> => {
      if (!clinicId) return null;

      const { data, error } = await getLooseTableClient("clinic_whatsapp_application_reminder_rules")
        .select("clinic_id, is_active, offset_unit, offset_value, send_time, timezone, updated_at")
        .eq("clinic_id", clinicId)
        .maybeSingle();

      if (error) throw error;
      return (data ?? null) as ClinicWhatsAppApplicationReminderRuleRow | null;
    },
    enabled: !!clinicId,
    staleTime: 1000 * 30,
  });
}

export function useNextWhatsAppApplicationReminderTrigger(clinicId: string | null) {
  return useQuery({
    queryKey: ["next-whatsapp-application-reminder-trigger", clinicId],
    queryFn: async (): Promise<NextWhatsAppApplicationReminderTriggerRow | null> => {
      if (!clinicId) return null;

      const { data, error } = await getLooseRpcClient().rpc("get_next_whatsapp_application_reminder_trigger", {
        _clinic_id: clinicId,
      });

      if (error) throw error;
      if (!Array.isArray(data) || data.length === 0) return null;

      return data[0] as NextWhatsAppApplicationReminderTriggerRow;
    },
    enabled: !!clinicId,
    staleTime: 1000 * 30,
  });
}

export function useUpsertClinicWhatsAppApplicationReminderRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      clinicId: string;
      rule: ClinicWhatsAppApplicationReminderRuleInput;
    }) => {
      const { clinicId, rule } = input;

      const payload = {
        clinic_id: clinicId,
        is_active: rule.isActive,
        offset_unit: rule.offsetUnit,
        offset_value: rule.offsetValue,
        send_time: rule.sendTime,
        timezone: rule.timezone || "America/Sao_Paulo",
      };

      const { error } = await getLooseTableClient("clinic_whatsapp_application_reminder_rules").upsert(payload, {
        onConflict: "clinic_id",
      });

      if (error) throw error;
      return true;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["clinic-whatsapp-application-reminder-rule", variables.clinicId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["next-whatsapp-application-reminder-trigger", variables.clinicId],
      });
    },
  });
}
